export type AuthUltimateStateType =
  'enter email' | 'enter password' | 'register form' | 'register form edit' | 'register confirm' | 'forgot password' | 'forgot password confirm' | 'verify email loading' | 'is auth'

type AuthLoginUltimateUserValues = {
  isAuth: boolean
  userId: string | None
  email: string | None
  name: string | None
  lastname: string | None
  isEmailVerified: boolean
}

type AuthLoginUltimateState = {
  emailInvalid: boolean
  mutation: undefined | keyof gen.InputBuyerAuth
  rerenderKey: number
  loading: boolean
  emailHolder: string
  password: string
  state: AuthUltimateStateType
  register: undefined | { register: gen.InputBuyerAuthRegisterValues; emailingSettings: None | gen.InputSubscriptionsEmailingSettings }
  loginRef: undefined | gen.InputAuthCodeRef
  registerRef: undefined | gen.InputAuthCodeRef
  forgotRef: undefined | gen.InputAuthCodeRef
  forgotNewPassword: string
  user: AuthLoginUltimateUserValues
}

function toUserValues(me: None | globalThis.UserValues): AuthLoginUltimateUserValues {
  return {
    isAuth: !!me?.isAuth,
    userId: me?.userId,
    email: me?.email,
    isEmailVerified: !!me?.isEmailVerified,
    name: me?.name,
    lastname: me?.lastname,
  }
}

export function useAuthLoginUltimate() {
  const state = useState('auth-login-ultimate', () =>
    shallowReactive<AuthLoginUltimateState>({
      emailInvalid: false,
      mutation: undefined,
      rerenderKey: 0,
      loading: true,
      emailHolder: '',
      password: '',
      state: 'enter email',
      register: undefined,
      loginRef: undefined,
      registerRef: undefined,
      forgotRef: undefined,
      forgotNewPassword: '',
      user: toUserValues(undefined),
    })
  )
  const data = state.value

  function reset(state: AuthUltimateStateType, options?: { simple?: boolean }) {
    data.state = state
    data.loading = false
    data.password = ''
    data.loginRef = undefined
    data.register = undefined
    data.registerRef = undefined
    data.emailInvalid = false
    data.forgotRef = undefined
    data.forgotNewPassword = ''

    if (!options?.simple) {
      data.rerenderKey += 1
      data.emailHolder = ''
    }
  }

  return { data, reset }
}

export function useAuthLoginUltimateSingleton() {
  const { data, reset } = useAuthLoginUltimate()
  const auth = useAuth()

  let prevResolvedUser: AuthLoginUltimateUserValues | undefined = data.user.userId ? data.user : undefined

  const authTimer = useTimeoutGlobal('auth-login-ultimate-auth')
  const emailTimer = useTimeoutGlobal('auth-login-ultimate-email')

  watch(
    () => data.emailHolder,
    () => {
      if (data.state === 'enter email' || data.state === 'is auth') {
        emailTimer.clear()
        return
      }

      emailTimer.set(() => {
        if (data.state === 'enter email' || data.state === 'is auth') return
        reset('enter email', { simple: true })
      }, 200)
    },
    { flush: 'sync' }
  )

  watch(
    () => toUserValues(auth.value),
    (doc) => {
      if (!doc.userId) {
        data.user = doc
        authTimer.clear()
        data.loading = true
        return
      }

      authTimer.set(() => {
        const prev = prevResolvedUser
        const hadUser = !!prev?.userId
        const userIdChanged = hadUser && prev?.userId !== doc.userId
        const userAuthChanged = hadUser && (prev?.email !== doc.email || prev?.isEmailVerified !== doc.isEmailVerified)

        prevResolvedUser = doc
        data.user = doc

        if (doc.isAuth) {
          if (!prev?.isAuth || userIdChanged || data.state !== 'is auth') reset('is auth')
        } else if (prev?.isAuth || userIdChanged || userAuthChanged || data.state === 'is auth') {
          reset('enter email')

          if (doc.email && !doc.isEmailVerified) {
            data.emailHolder = doc.email
            data.state = 'register form'
          }
        } else if (!hadUser && doc.email && !doc.isEmailVerified && data.state === 'enter email' && !data.emailHolder) {
          data.emailHolder = doc.email
          data.state = 'register form'
        }

        data.loading = false
      }, 50)
    },
    { immediate: true }
  )
}
